<template>
  <v-dialog
    :value="value"
    persistent
    max-width="30rem"
    content-class="customer-advance-dialog"
    @keydown.esc="cancel"
  >
    <v-card class="customer-advance-card">
      <v-toolbar flat>
        <v-toolbar-title>{{ title }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon :disabled="loading" title="Close" @click.prevent="cancel">
          <v-icon class="heading grey--text text--darken-4">close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <div class="fonthead pb-3">{{ message }}</div>

        <CustomerAdvanceAmountField
          v-model="amount"
          :label="label"
          :placeholder="placeholder"
          :hint="hint"
          :error="error"
          :max-amount="effectiveMax"
          :max-label="effectiveMaxLabel"
          :currency="currency"
          :disabled="loading"
          :fetching="fetching"
          @input="error = ''"
          @enter="answer(true)"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          class="button-width oms-dialog-action--secondary oms-btn oms-btn--secondary"
          :disabled="loading"
          @click.native="answer(false)"
          >{{ noText }}</v-btn
        >
        <v-btn
          class="white--text button-width oms-dialog-action--primary oms-btn oms-btn--primary"
          :loading="loading"
          :disabled="loading"
          @click.native="answer(true)"
          >{{ yesText }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { StorageKeys } from "@/constants/constants";
import { customerAdvance } from "@/constants/api-urls";
import {
  apiErrorMessage,
  limitToTwoDecimals,
  normaliseMax,
  validateAmount,
} from "@/utils/customerAdvance";
import CustomerAdvanceAmountField from "@/components/Common/CustomerAdvanceAmountField";
export default {
  name: "CustomerAdvancePopup",
  components: { CustomerAdvanceAmountField },
  props: {
    /* v-model, controls visibility */
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Customer Advance",
    },
    message: {
      type: String,
      default: "Is there any customer advance for this shipment?",
    },
    label: {
      type: String,
      default: "Customer Advance Amount",
    },
    placeholder: {
      type: String,
      default: "Enter amount",
    },
    hint: {
      type: String,
      default: "Enter the amount, or choose No if there is no advance.",
    },
    /* Yes needs an amount, No carries on with nothing entered. */
    requireAmountOnYes: {
      type: Boolean,
      default: true,
    },
    /* Ceiling for the amount. Null or 0 leaves it uncapped. */
    maxAmount: {
      type: [Number, String],
      default: null,
    },
    /* What the ceiling is called, used in the hint and the error. */
    maxLabel: {
      type: String,
      default: "Customer Amount",
    },
    /* Which record's saved advance prefills the field, "job" or
       "assignment". Leave both blank to open with an empty input. */
    advanceType: {
      type: String,
      default: "",
    },
    /* jobId for a job, the assignment _id for an assignment. */
    advanceId: {
      type: [String, Number],
      default: null,
    },
    /* shown as a suffix inside the input, e.g. AED */
    currency: {
      type: String,
      default: "",
    },
    noText: {
      type: String,
      default: "No",
    },
    yesText: {
      type: String,
      default: "Yes",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      amount: "",
      error: "",
      fetching: false,
      /* customerAmount from the API, the cap the backend enforces. */
      fetchedMax: null,
    };
  },
  computed: {
    effectiveMax() {
      return this.fetchedMax === null ? this.maxAmount : this.fetchedMax;
    },
    effectiveMaxLabel() {
      return this.fetchedMax === null ? this.maxLabel : "Customer Amount";
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.reset();
        this.fetchSavedAdvance();
      }
    },
  },
  methods: {
    reset() {
      this.amount = "";
      this.error = "";
      this.fetchedMax = null;
    },

    /* Prefills the field with the advance already saved against the job or
       the assignment. A failure leaves the field blank rather than blocking
       the flow, so the caller can still carry on. */
    fetchSavedAdvance() {
      if (!this.advanceType || !this.advanceId) return;
      this.fetching = true;
      const token = localStorage.getItem(StorageKeys.SessionKey);
      const params = { type: this.advanceType };
      if (this.advanceType == "assignment") {
        params.assignmentId = this.advanceId;
      } else {
        params.jobId = this.advanceId;
      }
      this.axios
        .get(`${this.constants.apiUrl}${customerAdvance}`, {
          headers: { authorization: `bearer ${token}` },
          params,
        })
        .then(
          (response) => {
            this.fetching = false;
            const body = response.data || {};
            const record = body.data || body;
            if (!record) return;
            const saved = record.customerAdvance;
            if (saved !== null && saved !== undefined && saved !== "") {
              this.amount = limitToTwoDecimals(saved);
            }
            /* Jobs and assignments both come back with the customer amount,
               which is the cap the API enforces. */
            const ceiling = normaliseMax(record.customerAmount);
            if (ceiling !== null) this.fetchedMax = ceiling;
          },
          (error) => {
            this.fetching = false;
            this.error = apiErrorMessage(error, "");
          }
        );
    },
    answer(confirmed) {
      let amount = null;
      if (confirmed) {
        const message = validateAmount({
          value: this.amount,
          max: this.effectiveMax,
          maxLabel: this.effectiveMaxLabel,
          currency: this.currency,
          required: this.requireAmountOnYes,
        });
        if (message) {
          this.error = message;
          return;
        }
        const raw = String(this.amount || "").trim();
        amount = raw ? Number(raw) : null;
      }
      this.$emit("input", false);
      this.$emit("confirm", { confirmed, amount });
    },
    cancel() {
      this.$emit("input", false);
      this.$emit("cancel");
    },
  },
};
</script>

<!-- Vuetify internals. Keyed on the dialog's content-class so it stays
     contained, and specific enough to beat the shared dialog input rules
     in oms-modern.css, which draw a full border and a focus ring. -->
<style>
.application .v-dialog.customer-advance-dialog .customer-advance-input .v-input__slot {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  margin-bottom: 0;
  padding: 0 !important;
}

.application
  .v-dialog.customer-advance-dialog
  .customer-advance-input.v-input--is-focused
  .v-input__slot {
  border: 0 !important;
  box-shadow: none !important;
}

/* Bottom line only: grey at rest, primary once focused. */
.application .v-dialog.customer-advance-dialog .customer-advance-input .v-input__slot::before {
  border-color: var(--oms-border) !important;
}

.application .v-dialog.customer-advance-dialog .customer-advance-input .v-input__slot::after {
  border-color: var(--oms-primary) !important;
}

.application .v-dialog.customer-advance-dialog .customer-advance-input .v-text-field__suffix {
  color: #64748b;
  font-size: 0.8125rem;
  padding-left: 0.5rem;
}

</style>
